import React from 'react';
import { Box, Typography, Paper, Chip, Button, alpha } from '@mui/material';
import {
  ArrowForward, Lock, CheckCircle,
  FlashOn, CreditCard, Construction, Receipt,
  AccountBalance, Apartment, Speed, WorkspacePremium,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../login/brand';
import { DASH, flowColor } from './dashboardColors';

// `unlockedBy` is the guided-step milestone (see guidedSteps.getUnlockState) that unlocks each program.
const fastFundingPrograms = [
  { key: 'mca', name: 'MCA', icon: FlashOn, range: '$50K - $2M', unlockedBy: 'application' },
  { key: 'loc', name: 'Business LOC', icon: CreditCard, range: '$50K - $250K', unlockedBy: 'bankStatements' },
  { key: 'equipment', name: 'Equipment', icon: Construction, range: '$25K - $500K', unlockedBy: 'bankStatements' },
  { key: 'invoice', name: 'Invoice Factoring', icon: Receipt, range: '$50K - $5M', unlockedBy: 'taxReturns' },
];

const sbaPrograms = [
  { key: 'sba7a', name: 'SBA 7(a)', icon: AccountBalance, range: '$250K - $5M', unlockedBy: 'pfs' },
  { key: 'sbaExpress', name: 'SBA Express', icon: Speed, range: '$50K - $500K', unlockedBy: 'pfs' },
  { key: 'sba504', name: 'SBA 504', icon: Apartment, range: '$500K - $5M+', unlockedBy: 'taxReturns' },
];

export const getPrograms = (type) => (type === 'sba' ? sbaPrograms : fastFundingPrograms);

export const countUnlockedPrograms = (type, unlockState = {}) => (
  getPrograms(type).filter((p) => unlockState[p.unlockedBy]).length
);

const ProgramsPreview = ({
  type = 'fastFunding',
  unlockState = {},
  onUnlockClick,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const accent = flowColor(type);

  const programs = getPrograms(type);
  const lockedPrograms = programs.filter((p) => !unlockState[p.unlockedBy]);
  const firstLocked = lockedPrograms[0];

  return (
    <Paper
      elevation={0}
      sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200', bgcolor: 'background.paper' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkspacePremium sx={{ color: accent, fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: DASH.ink }}>
            {t('dashboard.programsYouQualifyFor')}
          </Typography>
        </Box>
        {lockedPrograms.length > 0 && (
          <Chip
            size="small"
            icon={<Lock sx={{ fontSize: 14 }} />}
            label={`+${lockedPrograms.length} ${t('dashboard.moreWithDocs')}`}
            onClick={onUnlockClick || (() => navigate('/documents'))}
            sx={{
              bgcolor: alpha(accent, 0.1),
              color: accent,
              fontWeight: 600,
              '& .MuiChip-icon': { color: accent },
              '&:hover': { bgcolor: alpha(accent, 0.18) },
            }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pt: 1, pb: 1, px: 0.5 }}>
        {programs.map((program) => {
          const isUnlocked = !!unlockState[program.unlockedBy];
          const Icon = program.icon;

          return (
            <Box
              key={program.key}
              onClick={() => isUnlocked && navigate('/programs')}
              sx={{
                minWidth: 150,
                flex: '1 0 150px',
                p: 2,
                borderRadius: 2,
                border: '1.5px solid',
                borderColor: isUnlocked ? alpha(accent, 0.55) : 'grey.200',
                bgcolor: isUnlocked ? DASH.cream : 'grey.50',
                textAlign: 'center',
                position: 'relative',
                transition: 'all 0.2s ease',
                cursor: isUnlocked ? 'pointer' : 'default',
                '&:hover': isUnlocked ? {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 14px ${alpha(accent, 0.18)}`,
                } : {},
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: isUnlocked ? DASH.success : 'grey.400',
                  border: '2px solid #fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isUnlocked
                  ? <CheckCircle sx={{ fontSize: 14, color: 'white' }} />
                  : <Lock sx={{ fontSize: 11, color: 'white' }} />}
              </Box>

              <Icon sx={{ fontSize: '1.75rem', mb: 1, color: isUnlocked ? accent : 'grey.400' }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: isUnlocked ? DASH.ink : 'text.secondary', mb: 0.5 }}
              >
                {program.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isUnlocked ? DASH.brown : 'text.secondary', fontWeight: 600, display: 'block' }}
              >
                {program.range}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {isUnlocked ? (
                  <Chip
                    size="small"
                    label={t('dashboard.ready')}
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      bgcolor: alpha(DASH.success, 0.12),
                      color: DASH.success,
                    }}
                  />
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.3, display: 'block' }}>
                    {t('dashboard.unlocksWith', { step: t(`dashboard.unlockSteps.${program.unlockedBy}`) })}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {firstLocked ? (
        <Button
          fullWidth
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={onUnlockClick || (() => navigate('/documents'))}
          sx={{
            mt: 2,
            py: 1.25,
            borderRadius: 2,
            background: BRAND.gradient,
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { background: BRAND.gradientHover, boxShadow: 'none' },
          }}
        >
          {t(`dashboard.unlockHints.${firstLocked.unlockedBy}`, { program: firstLocked.name, range: firstLocked.range })}
        </Button>
      ) : (
        <Button
          fullWidth
          variant="text"
          endIcon={<ArrowForward />}
          onClick={() => navigate('/programs')}
          sx={{ mt: 2, color: accent, fontWeight: 600 }}
        >
          {t('dashboard.viewAllPrograms')}
        </Button>
      )}
    </Paper>
  );
};

export default ProgramsPreview;
